// Note: We use `number` instead of LineWeightType to be compatible with Vue types
import type {
  EntityProps,
  Layer,
  NoteConfig,
  HatchProp,
} from "@/types/legend.types";

// Re-export legend types as Drawing aliases for backwards compatibility
export type DrawingEntityProps = EntityProps;
export type DrawingLayerConfig = Layer;
export type DrawingNoteConfig = NoteConfig;

// Re-export HatchProp (now includes scale from legend.types)
export type { HatchProp };

/**
 * RGB color as comma-separated string (e.g., "255,0,0" for red).
 */
export type RGBColor = string;

/**
 * Color value - either an RGB string or a special value.
 * - RGB string: "255,0,0" for explicit color
 * - "kByLayer": Inherit color from layer
 * - "kByBlock": Inherit color from block
 */
export type DrawingColor = RGBColor | "kByLayer" | "kByBlock";

/**
 * Default note configuration used when no config is specified.
 */
export const DEFAULT_NOTE_CONFIG: DrawingNoteConfig = {
  id: "default-note-config",
  name: "DEFAULT",
  props: {
    color: "255,0,0", // Red
  },
};

/**
 * Parse an RGB color string to individual components.
 * @param color Color string like "255,0,0"
 * @returns Object with r, g, b values (0-255)
 */
export function parseRGBColor(color: RGBColor): {
  r: number;
  g: number;
  b: number;
} {
  const parts = color.split(",").map((s) => parseInt(s.trim(), 10));
  return {
    r: parts[0] ?? 0,
    g: parts[1] ?? 0,
    b: parts[2] ?? 0,
  };
}

/**
 * Check if a color is an RGB string (not a special value).
 */
export function isRGBColor(color: DrawingColor): color is RGBColor {
  return color !== "kByLayer" && color !== "kByBlock";
}

/**
 * Normalize a color value to an RGB string.
 * Handles both number arrays [r, g, b] and RGB strings "r,g,b".
 *
 * @param color - Color as number array or string
 * @returns RGB string like "255,0,0" or undefined
 */
export function normalizeColor(
  color: number[] | string | undefined,
): RGBColor | undefined {
  if (!color) return undefined;
  if (Array.isArray(color)) {
    return `${color[0]},${color[1]},${color[2]}`;
  }
  return color;
}
